import type {
  PhotoAdjustments,
  HslChannelId,
  HslChannelAdjustment,
  HslChannelAdjustments,
  CurvesAdjustment,
  CurvesChannelId,
  CurvesChannel,
  CurvesPoint,
} from '../types';
import type { Filter } from './filters';

// All HSL channel IDs for iteration
export const HSL_CHANNEL_IDS: HslChannelId[] = [
  'red', 'orange', 'yellow', 'green', 'aqua', 'blue', 'purple', 'magenta'
];

// Default single HSL channel adjustment
export const DEFAULT_HSL_CHANNEL: HslChannelAdjustment = {
  hue: 0,
  saturation: 0,
  luminance: 0,
};

// Default HSL channels (all zeroed)
export const DEFAULT_HSL_CHANNELS: HslChannelAdjustments = {
  red: { ...DEFAULT_HSL_CHANNEL },
  orange: { ...DEFAULT_HSL_CHANNEL },
  yellow: { ...DEFAULT_HSL_CHANNEL },
  green: { ...DEFAULT_HSL_CHANNEL },
  aqua: { ...DEFAULT_HSL_CHANNEL },
  blue: { ...DEFAULT_HSL_CHANNEL },
  purple: { ...DEFAULT_HSL_CHANNEL },
  magenta: { ...DEFAULT_HSL_CHANNEL },
};

// Default curves channel (linear: 0,0 to 1,1)
export const DEFAULT_CURVES_CHANNEL: CurvesChannel = {
  points: [
    { x: 0, y: 0 },
    { x: 1, y: 1 },
  ],
};

// Default curves adjustment
export const DEFAULT_CURVES: CurvesAdjustment = {
  enabled: false,
  channels: {
    rgb: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] },
    r: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] },
    g: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] },
    b: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] },
  },
};

export const DEFAULT_PHOTO_ADJUSTMENTS: PhotoAdjustments = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  hue: 0,
  temperature: 0,
  tint: 0,
  highlights: 0,
  shadows: 0,
  vibrance: 0,
  exposure: 0,
};

// Base keys (excludes nested hslChannels and curves)
const PHOTO_ADJUSTMENT_KEYS: Array<keyof PhotoAdjustments> = [
  'brightness', 'contrast', 'saturation', 'hue', 'temperature',
  'tint', 'highlights', 'shadows', 'vibrance', 'exposure'
];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeHue = (value: number) => {
  const raw = value % 360;
  return raw < 0 ? raw + 360 : raw;
};

const toSignedHue = (value: number) => {
  const normalized = normalizeHue(value);
  return normalized > 180 ? normalized - 360 : normalized;
};

const deriveFilterValues = (adj: PhotoAdjustments) => {
  const brightness = clamp(
    adj.brightness + adj.exposure + (adj.highlights + adj.shadows) * 0.35,
    -100,
    100
  );
  const contrast = clamp(adj.contrast + (adj.highlights - adj.shadows) * 0.2, -100, 100);
  const saturation = clamp(adj.saturation + adj.vibrance * 0.5 + adj.temperature * 0.1, -100, 100);
  const hue = adj.hue + adj.tint * 0.4 + adj.temperature * 0.35;

  return { brightness, contrast, saturation, hue };
};

// Deep equality for HSL channel adjustments
const areHslChannelsEqual = (
  a: HslChannelAdjustments | undefined,
  b: HslChannelAdjustments | undefined
): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return HSL_CHANNEL_IDS.every((id) => {
    const ca = a[id] ?? DEFAULT_HSL_CHANNEL;
    const cb = b[id] ?? DEFAULT_HSL_CHANNEL;
    return ca.hue === cb.hue && ca.saturation === cb.saturation && ca.luminance === cb.luminance;
  });
};

// Deep equality for curves points
const areCurvesPointsEqual = (a: CurvesPoint[], b: CurvesPoint[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((p, i) => p.x === b[i].x && p.y === b[i].y);
};

// Deep equality for curves adjustment
const areCurvesEqual = (
  a: CurvesAdjustment | undefined,
  b: CurvesAdjustment | undefined
): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.enabled !== b.enabled) return false;
  const channelIds: CurvesChannelId[] = ['rgb', 'r', 'g', 'b'];
  return channelIds.every((id) => {
    const ca = a.channels[id]?.points ?? DEFAULT_CURVES_CHANNEL.points;
    const cb = b.channels[id]?.points ?? DEFAULT_CURVES_CHANNEL.points;
    return areCurvesPointsEqual(ca, cb);
  });
};

export const arePhotoAdjustmentsEqual = (a: PhotoAdjustments, b: PhotoAdjustments): boolean => {
  // Check base numeric keys
  const baseEqual = PHOTO_ADJUSTMENT_KEYS.every((key) => {
    const va = a[key];
    const vb = b[key];
    // Only compare if both are numbers (skip nested objects)
    if (typeof va === 'number' && typeof vb === 'number') {
      return va === vb;
    }
    return (va ?? 0) === (vb ?? 0);
  });
  if (!baseEqual) return false;

  // Check HSL channels
  if (!areHslChannelsEqual(a.hslChannels, b.hslChannels)) return false;

  // Check curves
  if (!areCurvesEqual(a.curves, b.curves)) return false;

  return true;
};

export const photoAdjustmentsToFilters = (adj: PhotoAdjustments): Filter[] => {
  const { brightness, contrast, saturation, hue } = deriveFilterValues(adj);
  const filters: Filter[] = [];

  if (brightness !== 0) filters.push({ type: 'brightness', value: brightness });
  if (contrast !== 0) filters.push({ type: 'contrast', value: contrast });
  if (saturation !== 0) filters.push({ type: 'saturation', value: saturation });
  if (hue !== 0) filters.push({ type: 'hue-rotate', value: normalizeHue(hue) });

  return filters;
};

export const photoAdjustmentsToCssFilter = (adj: PhotoAdjustments): string => {
  const { brightness, contrast, saturation, hue } = deriveFilterValues(adj);
  const filters: string[] = [];

  if (brightness !== 0) filters.push(`brightness(${100 + brightness}%)`);
  if (contrast !== 0) filters.push(`contrast(${100 + contrast}%)`);
  if (saturation !== 0) filters.push(`saturate(${100 + saturation}%)`);
  if (hue !== 0) filters.push(`hue-rotate(${hue}deg)`);

  return filters.length > 0 ? filters.join(' ') : 'none';
};

export const photoAdjustmentsFromFilters = (filters?: Filter[]): PhotoAdjustments => {
  const next: PhotoAdjustments = { ...DEFAULT_PHOTO_ADJUSTMENTS };
  const list = Array.isArray(filters) ? filters : [];

  for (const filter of list) {
    const raw = Number(filter.value);
    const value = Number.isFinite(raw) ? raw : 0;
    switch (filter.type) {
      case 'brightness':
        next.brightness = clamp(value, -100, 100);
        break;
      case 'contrast':
        next.contrast = clamp(value, -100, 100);
        break;
      case 'saturation':
        next.saturation = clamp(value, -100, 100);
        break;
      case 'hue-rotate':
        next.hue = clamp(toSignedHue(value), -180, 180);
        break;
      default:
        break;
    }
  }

  return next;
};

// ==================== Deep Copy Helpers ====================

/** Deep copy HSL channel adjustments */
export const cloneHslChannels = (
  channels: HslChannelAdjustments | undefined
): HslChannelAdjustments => {
  if (!channels) return { ...DEFAULT_HSL_CHANNELS };
  const result = {} as HslChannelAdjustments;
  for (const id of HSL_CHANNEL_IDS) {
    const ch = channels[id] ?? DEFAULT_HSL_CHANNEL;
    result[id] = { hue: ch.hue, saturation: ch.saturation, luminance: ch.luminance };
  }
  return result;
};

/** Deep copy curves adjustment */
export const cloneCurves = (curves: CurvesAdjustment | undefined): CurvesAdjustment => {
  if (!curves) return { ...DEFAULT_CURVES, channels: { ...DEFAULT_CURVES.channels } };
  const channelIds: CurvesChannelId[] = ['rgb', 'r', 'g', 'b'];
  const channels = {} as Record<CurvesChannelId, CurvesChannel>;
  for (const id of channelIds) {
    const pts = curves.channels[id]?.points ?? DEFAULT_CURVES_CHANNEL.points;
    channels[id] = { points: pts.map((p) => ({ x: p.x, y: p.y })) };
  }
  return { enabled: curves.enabled, channels };
};

/** Deep copy full PhotoAdjustments (including nested) */
export const clonePhotoAdjustments = (adj: PhotoAdjustments): PhotoAdjustments => {
  const result: PhotoAdjustments = {
    brightness: adj.brightness,
    contrast: adj.contrast,
    saturation: adj.saturation,
    hue: adj.hue,
    temperature: adj.temperature,
    tint: adj.tint,
    highlights: adj.highlights,
    shadows: adj.shadows,
    vibrance: adj.vibrance,
    exposure: adj.exposure,
  };
  if (adj.hslChannels) {
    result.hslChannels = cloneHslChannels(adj.hslChannels);
  }
  if (adj.curves) {
    result.curves = cloneCurves(adj.curves);
  }
  return result;
};

// ==================== Non-Default Value Checks ====================

/** Check if any HSL channel has non-default values */
export const hasHslChannelChanges = (channels: HslChannelAdjustments | undefined): boolean => {
  if (!channels) return false;
  return HSL_CHANNEL_IDS.some((id) => {
    const ch = channels[id];
    if (!ch) return false;
    return ch.hue !== 0 || ch.saturation !== 0 || ch.luminance !== 0;
  });
};

/** Check if curves have non-default values (enabled and not linear) */
export const hasCurvesChanges = (curves: CurvesAdjustment | undefined): boolean => {
  if (!curves || !curves.enabled) return false;
  const channelIds: CurvesChannelId[] = ['rgb', 'r', 'g', 'b'];
  return channelIds.some((id) => {
    const pts = curves.channels[id]?.points;
    if (!pts) return false;
    // Linear is exactly 2 points at (0,0) and (1,1)
    if (pts.length !== 2) return true;
    const [p0, p1] = pts;
    return p0.x !== 0 || p0.y !== 0 || p1.x !== 1 || p1.y !== 1;
  });
};

// ==================== HSL Channel Color Mapping ====================

/** Hue ranges for each channel (in degrees, 0-360) */
export const HSL_CHANNEL_HUE_RANGES: Record<HslChannelId, { min: number; max: number; color: string }> = {
  red: { min: 345, max: 15, color: '#ef4444' },      // wraps around 0
  orange: { min: 15, max: 45, color: '#f97316' },
  yellow: { min: 45, max: 75, color: '#eab308' },
  green: { min: 75, max: 165, color: '#22c55e' },
  aqua: { min: 165, max: 195, color: '#06b6d4' },
  blue: { min: 195, max: 255, color: '#3b82f6' },
  purple: { min: 255, max: 285, color: '#a855f7' },
  magenta: { min: 285, max: 345, color: '#ec4899' },
};
